/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useEffect} from 'react'
import axios from 'axios'
import {API_URL} from '../../modules/auth/core/_requests'
import {useAuth} from '../../modules/auth'

// Define the interface for a single product variant from the backend
interface PleasureSpotProduct {
  parent_title: string
  variant_title: string
  sku: string
  barcode: string
  price: string
  compare_at_price: string
  inventory_quantity: number
  body_html: string
  vendor: string
  product_type: string
  tags: string
  status: string
  child_shopify_id: number
  parent_shopify_id: number
  parent_image_url: string
  variant_image_url: string
  live_product_url: string
  live_variant_url: string
  admin_product_url: string
  admin_variant_url: string
  created_at: string
  updated_at: string
}

// Interface for products grouped by their parent ID
interface GroupedPleasureSpotProduct {
  parentShopifyId: number
  parentTitle: string
  parentImageUrl?: string
  vendor: string
  productType: string
  status: string
  tags: string[]
  adminProductUrl?: string
  liveProductUrl?: string
  bodyHtml: string
  variants: PleasureSpotProduct[]
}

export const PleasureSpotPage: FC = () => {
  const [productsData, setProductsData] = useState<GroupedPleasureSpotProduct[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedVendor, setSelectedVendor] = useState<string>('')
  const [vendors, setVendors] = useState<string[]>([])
  const [selectedProductType, setSelectedProductType] = useState<string>('')
  const [productTypes, setProductTypes] = useState<string[]>([])
  const [tagSearch, setTagSearch] = useState<string>('')

  const fetchProducts = async (search = '', vendor = '', productType = '', tag = '') => {
    setLoading(true)
    setError(null)
    try {
      const params: any = {
        sortField: 'parent_title',
        sortOrder: 'ASC',
      }
      if (search) params.title = search
      if (vendor) params.vendor = vendor
      if (productType) params.productType = productType
      if (tag) params.tag = tag

      const response = await axios.get<{
        data: PleasureSpotProduct[]
        totalItems: number
      }>(`${API_URL}/api/pleasurespot/get-products`, {params})

      const grouped = response.data.data.reduce((acc, product) => {
        let entry = acc.get(product.parent_shopify_id)
        if (!entry) {
          entry = {
            parentShopifyId: product.parent_shopify_id,
            parentTitle: product.parent_title,
            parentImageUrl: product.parent_image_url,
            vendor: product.vendor,
            productType: product.product_type,
            status: product.status,
            tags: product.tags ? product.tags.split(',').map((t) => t.trim()) : [],
            adminProductUrl: product.admin_product_url,
            liveProductUrl: product.live_product_url,
            bodyHtml: product.body_html,
            variants: [],
          }
          acc.set(product.parent_shopify_id, entry)
        }
        entry.variants.push(product)
        return acc
      }, new Map<number, GroupedPleasureSpotProduct>())

      const allProducts = Array.from(grouped.values())
      setProductsData(allProducts)

      // Extract unique vendors for the filter dropdown (only on first load)
      if (vendors.length === 0 && response.data.data.length > 0) {
        const uniqueVendors = Array.from(
          new Set(response.data.data.map((item) => item.vendor).filter(Boolean))
        ).sort()
        setVendors(uniqueVendors)
      }

      if (productTypes.length === 0 && response.data.data.length > 0) {
        const uniqueProductTypes = Array.from(
          new Set(response.data.data.map((item) => item.product_type).filter(Boolean))
        ).sort()
        setProductTypes(uniqueProductTypes)
      }
    } catch (err) {
      console.error('Error fetching PleasureSpot products:', err)
      setError('Failed to fetch product data. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProducts(searchTerm, selectedVendor, selectedProductType, tagSearch)
    }, 500)
    return () => clearTimeout(handler)
  }, [searchTerm, selectedVendor, selectedProductType, tagSearch])

  if (loading && productsData.length === 0) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return <div className='alert alert-danger ms-3 me-3'>{error}</div>
  }

  return (
    <div className='p-3'>
      {/* Filters */}
      <div className='card mb-4 shadow-sm'>
        <div className='card-body'>
          <div className='row g-3 align-items-center'>
            <div className='col-md-4'>
              <label className='form-label small fw-semibold text-muted mb-1'>Search Products</label>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder='Search by product title...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className='col-md-2'>
              <label className='form-label small fw-semibold text-muted mb-1'>Filter by Vendor</label>
              <select
                className='form-select form-select-sm'
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
              >
                <option value=''>All Vendors</option>
                {vendors.map((vendor) => (
                  <option key={vendor} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2'>
              <label className='form-label small fw-semibold text-muted mb-1'>Product Type</label>
              <select
                className='form-select form-select-sm'
                value={selectedProductType}
                onChange={(e) => setSelectedProductType(e.target.value)}
              >
                <option value=''>All Types</option>
                {productTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2'>
              <label className='form-label small fw-semibold text-muted mb-1'>Tag</label>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder='Search by tag...'
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
              />
            </div>
            <div className='col-md-2 text-end'>
              <label className='form-label small fw-semibold text-muted mb-1'>Results</label>
              <div className='small'>
                <span className='badge bg-primary fs-6'>{productsData.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className='row g-4'>
        {productsData.length > 0 ? (
          productsData.map((product) => (
            <div key={product.parentShopifyId} className='col-12 col-md-6 col-lg-4 col-xl-3'>
              <div className='card h-100 shadow-sm'>
                {product.parentImageUrl && (
                  <img
                    src={product.parentImageUrl}
                    className='card-img-top'
                    alt={product.parentTitle}
                    style={{height: '250px', objectFit: 'contain', paddingTop: '10px'}}
                  />
                )}
                <div className='card-body d-flex flex-column'>
                  <h5 className='card-title'>{product.parentTitle}</h5>
                  <div className='mb-2'>
                    <span className='fw-bold me-2'>Vendor:</span>
                    <span>{product.vendor}</span>
                  </div>
                  <div className='mb-2'>
                    <span className='fw-bold me-2'>Type:</span>
                    <span>{product.productType}</span>
                  </div>
                  <div className='mb-2'>
                    <span className={`badge bg-${product.status === 'active' ? 'success' : 'warning'}`}>
                      {product.status}
                    </span>
                  </div>
                  <div className='mb-2'>
                    {product.tags.slice(0, 5).map((tag) => (
                      <span key={tag} className='badge bg-secondary me-1 mb-1'>
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Links */}
                  <div className='d-flex align-items-center mb-3'>
                    {product.adminProductUrl && (
                      <a
                        href={product.adminProductUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='btn btn-sm btn-icon'
                        title='View on Shopify'
                      >
                        <img
                          src='/media/logos/shopify-icon.png'
                          alt='Shopify'
                          style={{height: '24px'}}
                        />
                      </a>
                    )}
                    {product.liveProductUrl && (
                      <a
                        href={product.liveProductUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='btn btn-sm btn-icon'
                        title='View Live Product'
                      >
                        <i className='fas fa-external-link-alt fs-3'></i>
                      </a>
                    )}
                  </div>

                  <div
                    className='variants-container mt-auto flex-grow-1'
                    style={{maxHeight: '150px', overflowY: 'auto'}}
                  >
                    <h6>Variants:</h6>
                    {product.variants.map((variant) => (
                      <div key={variant.child_shopify_id} className='mb-2 border-bottom pb-1'>
                        <div className='d-flex align-items-center'>
                          {variant.variant_image_url && (
                            <img
                              src={variant.variant_image_url}
                              alt={variant.variant_title}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'contain',
                                marginRight: '8px',
                              }}
                            />
                          )}
                          <div className='flex-grow-1'>
                            <div className='fw-bold'>{variant.variant_title}</div>
                            <small className='text-muted' style={{fontSize: '0.7rem'}}>
                              SKU: {variant.sku}
                            </small>
                            <div>
                              <span className='badge bg-primary me-1' style={{fontSize: '0.7rem'}}>
                                ${variant.price}
                              </span>
                              <span
                                className={`badge bg-${
                                  variant.inventory_quantity > 0 ? 'info' : 'danger'
                                }`}
                                style={{fontSize: '0.7rem'}}
                              >
                                Stock: {variant.inventory_quantity}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className='col-12'>
            <div className='alert alert-info text-center'>No products match your current filters.</div>
          </div>
        )}
      </div>
    </div>
  )
}

export const PleasreSpotProductsWrapper: FC = () => {
  const {currentUser} = useAuth()

  return (
    <>
      <div className='d-flex justify-content-between align-items-center mt-3 ms-3 me-3'>
        <h1>PleasureSpot Products - {currentUser?.decoded?.current_shopify_store || 'No Store Selected'}</h1>
      </div>
      <PleasureSpotPage />
    </>
  )
}
